// rollenKatalog.ts — die Rollen, die ein Kontakt an einem Schaden, einem Risiko
// oder einem Vertrag einnimmt, als zweite Stufe unter „Sonstige".
//
// Herkunft: `kmvs-data/Kontakte und Menschen fürs Unternehmen NEU-1.xlsx`,
// Blatt „Kontakte", Spalten P (Rolle im Versicherungsvertrag), Q (Rolle an
// Risiko / Risikoort) und R (Rolle im Schaden). Die Werte stehen dort WÖRTLICH
// so — inklusive der Kleinschreibung bei „nicht versichert", „versicherte
// Person" und „sonstiger Dienstleister VR". Sie wurde nicht begradigt: die
// Strings sind zugleich `ContactStatus.status` und werden zeichengenau gegen
// `apps/api/src/contactStatus.katalog.ts` geprüft (`rollenKatalog.spec.ts`).
//
// Warum eine zweite Stufe statt einer langen Liste: Der PO hat am 09.09.2026
// beim Durchgehen der Excel festgestellt, dass „vieles … nach einer Rolle im
// Schaden oder Risiko" geht, und daraufhin verlangt: „Dann würde ich da einen
// Button machen Schaden." Eine flache Auswahl hätte über 40 Einträge — die
// Gruppen machen sie navigierbar und sagen zugleich, WO die Rolle entsteht.
//
// Die drei Gruppennamen sind KEINE Kontaktstatus. Sie erreichen `statusName`
// nie: die Payload-Builder schicken die gewählte ROLLE, und ohne Rolle fallen
// sie auf „Sonstige" zurück. Stünde eine Gruppe im Feld, verwürfe
// `ContactService.resolveRelations` sie ersatzlos — Kontakt ohne Status,
// HTTP 201, keine Fehlermeldung.

/** Rollen aus Excel-Spalte R. */
import {
  STATUS_ALTINTERESSENT,
  STATUS_ALTKUNDE,
  STATUS_BEZIEHUNG,
  STATUS_DIREKTIONSPARTNER,
  STATUS_IMMOBILIENBEZOGEN,
  STATUS_LIEFERANT_DIENSTLEISTER,
  STATUS_SPONSOR_SPENDER,
  STATUS_VEREIN_VERBAND,
  STATUS_EMPFEHLUNGSGEBER,
  STATUS_HAUSHALTSMITGLIED,
  STATUS_LIEFERANT,
  STATUS_MENSCH_IM_UNTERNEHMEN,
  STATUS_MFU,
  STATUS_MITARBEITER_COURTE,
  STATUS_MITGLIEDSCHAFT,
  STATUS_SPONSORING,
  STATUS_VERMITTLER,
} from "./sonstigeStatusKatalog";
import { STATUS_AP_PRODUKTPARTNER, STATUS_PRODUKTPARTNER } from "./produktpartnerKatalog";

export const GRUPPE_SCHADEN = "Rolle im Schaden";
/** Rollen aus Excel-Spalte Q. */
export const GRUPPE_RISIKO = "Rolle am Risiko";
/** Rollen aus Excel-Spalte P. */
export const GRUPPE_VERTRAG = "Rolle im Vertrag";

export type RollenOption = {
  /** Der gespeicherte Wert = `ContactStatus.status`. */
  value: string;
  /** Beschriftung. Klammerzusätze gehören ausschließlich hierhin. */
  label: string;
};

/**
 * Rolle im Schaden — für Person und Firma gleich.
 *
 * Der PO hat am 09.09.2026 zu den ersten dreien gesagt, sie entstünden
 * ausschließlich am Schaden selbst („den Anspruchsteller … kannst du nur zum
 * Schaden"), zu Reparaturfirma, Rechtsanwalt, Sachverständigem und sonstigem
 * Dienstleister dagegen, die müsse man auch vorab anlegen können („wir können
 * ja eine Reparaturfirma auch so hinterlegen, ohne dass die zu einem Schaden
 * [gehört]"). Angeboten wird hier trotzdem die VOLLSTÄNDIGE Spalte — so steht
 * es in der Excel, und so hat der User es am 11.09.2026 bestellt.
 */
export const ROLLEN_SCHADEN: RollenOption[] = [
  { value: "Anspruchsteller", label: "Anspruchsteller" },
  { value: "Geschädigter", label: "Geschädigter" },
  { value: "Schadenverursacher", label: "Schadenverursacher" },
  { value: "Halter", label: "Halter" },
  { value: "Reparaturfirma", label: "Reparaturfirma" },
  { value: "Rechtsanwalt im Schaden", label: "Rechtsanwalt im Schaden" },
  { value: "Sachverständiger", label: "Sachverständiger" },
  { value: "Regulierer VR", label: "Regulierer VR" },
  { value: "Sachbearbeiter VR", label: "Sachbearbeiter VR" },
  { value: "sonstiger Dienstleister VR", label: "sonstiger Dienstleister VR" },
  { value: "Immobilienmieter", label: "Immobilienmieter" },
];

/**
 * Rolle an Risiko / Risikoort — für Person und Firma gleich.
 *
 * Die Excel gliedert die Spalte in drei Blöcke (allgemein, „zusätzlich bei
 * Immobilien", „zusätzlich bei Kfz"). Die Auswahl ist flach, der Block steht
 * im Klammerzusatz — ein zweistufiges Dropdown im zweistufigen Dropdown wäre
 * eine Stufe zu viel.
 *
 * „Halter" trägt denselben Wert wie in [[ROLLEN_SCHADEN]]: es ist derselbe
 * Kontaktstatus, erreichbar über zwei Wege. Der Filter entdoppelt ihn
 * (`rollenFilterOptionen`).
 *
 * „Bank/Finanzierer" stammt NICHT aus der Excel, sondern aus der Mail von
 * DC vom 10.09.2026 („Bank/Finanzierer (Hausbank, Kreditgeber, Leasinggeber,
 * …)"). Er steht beim Risiko, weil der User ihn dort verortet hat.
 *
 * „Erbbaurechtsgeber", „Nießbrauchgeber" und „Zwangsverwalter" kamen mit der
 * Fachliste vom 12.09.2026 nach: zu den beiden „-berechtigten" gehört jeweils
 * die Gegenseite, der Zwangsverwalter fehlte ganz. Angelegt von Migration
 * `202609130001-AddRestlicheRisikoRollen`.
 */
export const ROLLEN_RISIKO: RollenOption[] = [
  { value: "Eigentümer", label: "Eigentümer (Anteil in % am Risiko)" },
  { value: "Betreiber", label: "Betreiber" },
  { value: "Nutzer", label: "Nutzer" },
  { value: "Verwalter", label: "Verwalter" },
  { value: "Leasingnehmer", label: "Leasingnehmer" },
  { value: "Leasinggeber", label: "Leasinggeber" },
  { value: "Bank/Finanzierer", label: "Bank/Finanzierer (Hausbank, Kreditgeber, Leasinggeber)" },
  { value: "Mieter", label: "Mieter (Immobilie)" },
  { value: "Vermieter", label: "Vermieter (Immobilie)" },
  { value: "Pächter", label: "Pächter (Immobilie)" },
  { value: "Verpächter", label: "Verpächter (Immobilie)" },
  { value: "Erbbauberechtigter", label: "Erbbauberechtigter (Immobilie)" },
  { value: "Erbbaurechtsgeber", label: "Erbbaurechtsgeber (Immobilie)" },
  { value: "Nießbrauchberechtigter", label: "Nießbrauchberechtigter (Immobilie)" },
  { value: "Nießbrauchgeber", label: "Nießbrauchgeber (Immobilie)" },
  { value: "Zwangsverwalter", label: "Zwangsverwalter (Immobilie)" },
  { value: "Halter", label: "Halter (Kfz)" },
];

/**
 * Rolle im Versicherungsvertrag, PERSON (Excel-Zeile 2).
 *
 * Die Excel notiert „ggf. Bezugsberechtigter" — das „ggf." ist ein Hinweis auf
 * die Sparte, kein Namensbestandteil, und steht deshalb nicht im Wert.
 */
export const ROLLEN_VERTRAG_PERSON: RollenOption[] = [
  { value: "Versicherungsnehmer", label: "Versicherungsnehmer" },
  { value: "Mitversicherungsnehmer", label: "Mitversicherungsnehmer" },
  { value: "versicherte Person", label: "versicherte Person" },
  { value: "mitversicherte Person", label: "mitversicherte Person" },
  { value: "nicht versichert", label: "nicht versichert" },
  { value: "Bezugsberechtigter", label: "Bezugsberechtigter" },
];

/** Rolle im Versicherungsvertrag, FIRMA (Excel-Zeile 3). */
export const ROLLEN_VERTRAG_FIRMA: RollenOption[] = [
  { value: "Versicherungsnehmer", label: "Versicherungsnehmer" },
  { value: "Mitversicherungsnehmer", label: "Mitversicherungsnehmer" },
  { value: "nicht versichert", label: "nicht versichert" },
  { value: "mitversichertes Unternehmen", label: "mitversichertes Unternehmen" },
  {
    value: "Hypotheken-/Grundpfandrechtsgläubiger",
    label: "Hypotheken-/Grundpfandrechtsgläubiger (nur bei Immobilien)",
  },
];

/** Die drei Gruppen in Anzeigereihenfolge — Schaden zuerst (PO-Reihenfolge). */
export const ROLLEN_GRUPPEN: string[] = [GRUPPE_SCHADEN, GRUPPE_RISIKO, GRUPPE_VERTRAG];

/**
 * Die Gruppen als Einträge für das ERSTE Dropdown.
 *
 * Sie liegen bewusst hier und nicht in `sonstigeStatusKatalog.ts`: dort prüft
 * `sonstigeKontaktstatus.spec.ts` jeden `value:` gegen den Backend-Katalog,
 * und eine Gruppe hat dort nichts verloren.
 */
export const ROLLEN_GRUPPEN_OPTIONS: RollenOption[] = ROLLEN_GRUPPEN.map((g) => ({
  value: g,
  label: `${g} …`,
}));

/** Ist der gewählte Untertyp eine Gruppe, hinter der noch eine Rolle fehlt? */
export const istRollenGruppe = (wert: string): boolean => ROLLEN_GRUPPEN.includes(wert);

/**
 * Die Rollen einer Gruppe. Nur der Vertrag unterscheidet Person und Firma —
 * eine Person ist „versicherte Person", eine Firma „mitversichertes
 * Unternehmen", und der Grundpfandrechtsgläubiger ist immer eine Bank.
 */
export const rollenFuer = (gruppe: string, istFirma: boolean): RollenOption[] => {
  if (gruppe === GRUPPE_SCHADEN) return ROLLEN_SCHADEN;
  if (gruppe === GRUPPE_RISIKO) return ROLLEN_RISIKO;
  if (gruppe === GRUPPE_VERTRAG) {
    return istFirma ? ROLLEN_VERTRAG_FIRMA : ROLLEN_VERTRAG_PERSON;
  }
  return [];
};

/** Zu welcher Gruppe gehört eine bereits gespeicherte Rolle? */
export const gruppeVonRolle = (rolle: string): string => {
  if (ROLLEN_SCHADEN.some((r) => r.value === rolle)) return GRUPPE_SCHADEN;
  if (ROLLEN_RISIKO.some((r) => r.value === rolle)) return GRUPPE_RISIKO;
  if (
    ROLLEN_VERTRAG_PERSON.some((r) => r.value === rolle) ||
    ROLLEN_VERTRAG_FIRMA.some((r) => r.value === rolle)
  ) {
    return GRUPPE_VERTRAG;
  }
  return "";
};

/**
 * Jeder Rollenwert genau einmal, in Gruppenreihenfolge.
 *
 * „Halter" steht in zwei Gruppen und „Versicherungsnehmer" in beiden
 * Vertragslisten — als Kontaktstatus ist das jeweils EIN Wert. Ohne das
 * Entdoppeln stünde er im Spaltenfilter zweimal.
 */
export const ALLE_ROLLEN: string[] = Array.from(
  new Set(
    [
      ...ROLLEN_SCHADEN,
      ...ROLLEN_RISIKO,
      ...ROLLEN_VERTRAG_PERSON,
      ...ROLLEN_VERTRAG_FIRMA,
    ].map((r) => r.value),
  ),
);

/**
 * Hängt die drei Gruppen an eine Unterkategorien-Liste.
 *
 * Sie stehen ganz unten, weil sie kein Status sind, sondern ein Abzweig: erst
 * nach der Wahl erscheint das zweite Dropdown. Die Funktion lebt hier und
 * nicht in `sonstigeStatusKatalog.ts`, damit die Gruppennamen gar nicht erst
 * in die Datei geraten, deren `value:`-Einträge gegen den Backend-Katalog
 * geprüft werden.
 */
export const mitRollenGruppen = <T extends RollenOption>(
  basis: readonly T[],
): RollenOption[] => [...basis, ...ROLLEN_GRUPPEN_OPTIONS];

/**
 * Jede Rolle genau einmal, mit Beschriftung — für den Spaltenfilter der
 * Kontaktliste.
 *
 * Das Label ist das der ERSTEN Fundstelle. Bei „Halter" heißt das: die
 * Schaden-Variante ohne Klammerzusatz gewinnt, denn im Filter geht es um den
 * Status, nicht darum, an welchem Objekt er entstanden ist.
 */
export const ALLE_ROLLEN_OPTIONS: RollenOption[] = (() => {
  const gesehen = new Set<string>();
  const out: RollenOption[] = [];
  for (const r of [
    ...ROLLEN_SCHADEN,
    ...ROLLEN_RISIKO,
    ...ROLLEN_VERTRAG_PERSON,
    ...ROLLEN_VERTRAG_FIRMA,
  ]) {
    if (gesehen.has(r.value)) continue;
    gesehen.add(r.value);
    out.push(r);
  }
  return out;
})();

/** Die Rollenwerte einer Gruppe — ohne Doppelung zwischen Person und Firma. */
export const rollenWerteDerGruppe = (gruppe: string): string[] => {
  if (gruppe === GRUPPE_VERTRAG) {
    return Array.from(
      new Set([...ROLLEN_VERTRAG_PERSON, ...ROLLEN_VERTRAG_FIRMA].map((r) => r.value)),
    );
  }
  return rollenFuer(gruppe, false).map((r) => r.value);
};

/* ------------------------------------------------------------------ *
 * Eine Liste statt zweier Dropdowns
 * ------------------------------------------------------------------ */

/**
 * Die drei Überschriften über den Unterkategorien — aus der Fachliste vom
 * 12.09.2026. Sie sind wie die Rollengruppen NUR Navigationshilfen und dürfen
 * `statusName` nie erreichen; `rollenKatalog.spec.ts` prüft das.
 */
export const GRUPPE_PRIVAT = "Beziehung zu Privatkontakten (im und außerhalb des Haushaltes)";
export const GRUPPE_UNTERNEHMEN =
  "Unternehmens-Verbindung (Mensch mit und ohne Verantwortung / Arbeitnehmer / Beteiligter)";
export const GRUPPE_COURTE = "Verbindung zu Courté";
/**
 * Rollen im Umfeld des Kunden — sein Lieferant, sein Vermieter, sein Verein.
 *
 * Bewusst getrennt von {@link GRUPPE_COURTE}: Dort geht es um Beziehungen zu
 * UNS, hier um Beziehungen des Kunden. Genau an dieser Grenze hängt auch die
 * CO-intern-Regel — „Lieferant" (Courté) macht einen Kontakt unsichtbar,
 * „Lieferant/Dienstleister" (Kunde) nicht.
 */
export const GRUPPE_UMFELD = "Umfeld des Kunden";
/** Auffangbecken: ein Untertyp, den niemand zugeordnet hat, verschwindet nicht. */
export const GRUPPE_WEITERE = "Weitere";

/** Eine Zeile der Auswahlliste, mit der Überschrift, unter der sie steht. */
export type GruppierteOption = {
  value: string;
  label: string;
  /** Überschrift des Abschnitts. */
  bereich: string;
};

/**
 * Welcher Untertyp unter welche Überschrift gehört.
 *
 * Die Zuordnung folgt der Fachliste: „Wer ist das für uns?" (Courté),
 * „Wie hängt der Kontakt an einer Privatperson?" und „… an einem Unternehmen?".
 * Was hier fehlt, landet unter {@link GRUPPE_WEITERE} — ein neuer Untertyp ist
 * dann unsortiert, aber nie unsichtbar.
 */
const GRUPPE_JE_STATUS: Record<string, string> = {
  [STATUS_VERMITTLER]: GRUPPE_COURTE,
  [STATUS_EMPFEHLUNGSGEBER]: GRUPPE_COURTE,
  [STATUS_PRODUKTPARTNER]: GRUPPE_COURTE,
  [STATUS_AP_PRODUKTPARTNER]: GRUPPE_COURTE,
  [STATUS_LIEFERANT]: GRUPPE_COURTE,
  [STATUS_SPONSORING]: GRUPPE_COURTE,
  [STATUS_MITGLIEDSCHAFT]: GRUPPE_COURTE,
  [STATUS_DIREKTIONSPARTNER]: GRUPPE_COURTE,
  [STATUS_MITARBEITER_COURTE]: GRUPPE_COURTE,
  [STATUS_BEZIEHUNG]: GRUPPE_PRIVAT,
  [STATUS_HAUSHALTSMITGLIED]: GRUPPE_PRIVAT,
  [STATUS_MENSCH_IM_UNTERNEHMEN]: GRUPPE_UNTERNEHMEN,
  [STATUS_MFU]: GRUPPE_UNTERNEHMEN,
  // Neu 14.09.2026. Altkunde und Altinteressent beschreiben UNSERE frühere
  // Beziehung und stehen deshalb bei Courté, nicht im Umfeld des Kunden.
  [STATUS_ALTKUNDE]: GRUPPE_COURTE,
  [STATUS_ALTINTERESSENT]: GRUPPE_COURTE,
  [STATUS_SPONSOR_SPENDER]: GRUPPE_COURTE,
  [STATUS_LIEFERANT_DIENSTLEISTER]: GRUPPE_UMFELD,
  [STATUS_IMMOBILIENBEZOGEN]: GRUPPE_UMFELD,
  [STATUS_VEREIN_VERBAND]: GRUPPE_UMFELD,
};

/** Reihenfolge der Abschnitte in der Auswahlliste. */
export const AUSWAHL_GRUPPEN: string[] = [
  GRUPPE_PRIVAT,
  GRUPPE_UNTERNEHMEN,
  GRUPPE_COURTE,
  GRUPPE_UMFELD,
  GRUPPE_WEITERE,
  ...ROLLEN_GRUPPEN,
];

/**
 * Unterkategorien UND Rollen in einer einzigen, gruppierten Liste.
 *
 * Das ist der Ersatz für die zwei gestapelten Dropdowns: Wer „Reparaturfirma"
 * sucht, tippt es — statt vorher zu erraten, dass sie unter „Rolle im Schaden"
 * steckt. Die Basisliste kommt von der Maske, weil sie rechteabhängig ist
 * (`sonstigeOptionenFirma` blendet die CO-internen Arten ohne `cointern:read`
 * aus); die Reihenfolge innerhalb einer Gruppe bleibt die der Basisliste.
 */
export function sonstigeAuswahlGruppiert(
  basis: readonly RollenOption[],
  istFirma: boolean,
): GruppierteOption[] {
  const ausBasis = (gruppe: string): GruppierteOption[] =>
    basis
      .filter((o) => (GRUPPE_JE_STATUS[o.value] ?? GRUPPE_WEITERE) === gruppe)
      .map((o) => ({ value: o.value, label: o.label, bereich: gruppe }));

  const ausRollen = (gruppe: string): GruppierteOption[] =>
    rollenFuer(gruppe, istFirma).map((r) => ({
      value: r.value,
      label: r.label,
      bereich: gruppe,
    }));

  return AUSWAHL_GRUPPEN.flatMap((g) =>
    ROLLEN_GRUPPEN.includes(g) ? ausRollen(g) : ausBasis(g),
  );
}
